// 실기기 점검(#111). functions/src/qa 응답 형식과 이슈 판정 규칙(isori-automation docs/tc-format.md §3).

export type Platform = 'android' | 'ios';
export const PLATFORMS: Platform[] = ['android', 'ios'];
export const PLATFORM_LABEL: Record<Platform, string> = { android: 'Android', ios: 'iOS' };

export type QaResultValue = 'pass' | 'fail' | 'skip';
export const RESULT_LABEL: Record<QaResultValue, string> = { pass: '통과', fail: '실패', skip: '건너뜀' };

export interface TcItem {
  id: string;
  title: string;
  steps: string[];
  expect: string;
  ifFail: string;
  required: string[];
  related: string[];
  platforms: Platform[];
  optional: boolean;
  core: boolean;
  /** 데이터를 지우는 조작 */
  destructive?: boolean;
}

export interface TcStage {
  id: string;
  title: string;
  note: string;
  setup: string[];
  items: TcItem[];
}

export interface TcIssueView {
  key: string;
  repo: string;
  number: number;
  title: string;
  tcUrl: string | null;
  /** 보드 상태 라벨 값(status: …). 라벨을 못 읽으면 TC를 만들 때의 상태 */
  status: string | null;
  state: 'open' | 'closed' | null;
  url: string | null;
  statusError: string | null;
}

export interface TcView {
  name: string;
  /** 결과 파일 results/<id>.json의 id */
  id: string;
  sha: string | null;
  tc: {
    id?: string | null;
    title?: string;
    deployment: { date: string | null; builds: { app: string; appId: string | null; repo?: string | null; pr: number | string | null }[] };
    notes?: string[];
    issues: { key: string }[];
    stages: TcStage[];
  };
  warnings: string[];
  issues: TcIssueView[];
}

/** results/<id>.json의 한 칸(isori-automation이 원본) */
export interface QaResult {
  itemId: string;
  platform: Platform;
  result: QaResultValue;
  note: string;
  /** GitHub 로그인(어드민 계정과 매핑, 없으면 이메일) */
  by: string;
  at: string;
}

/** 저장할 한 칸. result null은 지우기 */
export interface QaChange {
  itemId: string;
  platform: Platform;
  result: QaResultValue | null;
  note: string;
}

export const cellKey = (itemId: string, platform: Platform) => `${itemId}|${platform}`;
export type ResultMap = Map<string, QaResult>;
export const toMap = (rs: readonly QaResult[]): ResultMap => new Map(rs.map((r) => [cellKey(r.itemId, r.platform), r]));

export const allItems = (stages: readonly TcStage[]) => stages.flatMap((s) => s.items);

export interface IssueVerdict {
  /** done: Done 가능, failed: 실패, checking: 확인 중, none: 필수 칸 없음 */
  kind: 'done' | 'failed' | 'checking' | 'none';
  failed: number;
  passed: number;
  /** 필수 칸 수(항목 × 그 항목의 플랫폼, 선택 항목 제외) */
  total: number;
}

/**
 * 이슈 판정(docs/tc-format.md §3).
 * 필수 칸 = 그 이슈를 required에 가진 항목(선택 항목 제외) × 항목의 platforms.
 * fail이 하나라도 있으면 실패, 모두 pass면 Done 가능, 아니면 확인 중(통과 / 필수 칸). skip은 통과로 치지 않는다.
 */
export function issueVerdict(stages: readonly TcStage[], key: string, results: ResultMap): IssueVerdict {
  let failed = 0;
  let passed = 0;
  let total = 0;
  for (const item of allItems(stages)) {
    if (item.optional || !item.required.includes(key)) continue;
    for (const p of item.platforms) {
      total++;
      const r = results.get(cellKey(item.id, p))?.result;
      if (r === 'pass') passed++;
      if (r === 'fail') failed++;
    }
  }
  const kind = !total ? 'none' : failed > 0 ? 'failed' : passed === total ? 'done' : 'checking';
  return { kind, failed, passed, total };
}

/** 항목의 칸 중 결과가 없는 칸이 있는지(플랫폼 필터 반영) */
export const itemPending = (item: TcItem, results: ResultMap, platform: Platform | null) =>
  item.platforms.filter((p) => !platform || p === platform).some((p) => !results.get(cellKey(item.id, p))?.result);

export const STATUS_COLOR: Record<string, string> = {
  ready: 'default',
  'in-progress': 'processing',
  'code-review': 'purple',
  acceptance: 'warning',
  done: 'success',
};
